import { AlertTriangle, ShieldX, TrendingDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Critical Alerts — dashboard parity with the mobile app's home Critical Alerts
 * section. One alert per asset: warranty expired (priority) OR upgrade
 * recommended (healthScore < 6.6 and not replaced). Hidden when there are none.
 *
 * Structural input type so it accepts whatever asset shape the dashboard holds.
 */
export interface AlertAsset {
  id: string;
  name: string;
  healthScore?: number;
  lifecycleStatus?: string;
  warrantyExpiresAt?: string;
  manufacturerWarrantyExpiry?: string;
  manufacturerWarrantyStatus?: string;
  /** Backend-computed warranty status ('active' | 'expiringSoon' | 'expired' | …) — most reliable signal. */
  warrantyStatus?: string;
}

type AlertType = 'warranty' | 'upgrade';
interface CriticalAlert {
  assetId: string;
  type: AlertType;
  title: string;
  detail: string;
}

export function computeCriticalAlerts(assets: AlertAsset[]): CriticalAlert[] {
  const now = Date.now();
  const alerts: CriticalAlert[] = [];
  for (const a of assets) {
    const expiryRaw = a.manufacturerWarrantyExpiry ?? a.warrantyExpiresAt;
    const expiry = expiryRaw ? Date.parse(expiryRaw) : NaN;
    const warrantyExpired =
      a.warrantyStatus?.toLowerCase() === 'expired' ||
      a.manufacturerWarrantyStatus?.toLowerCase() === 'expired' ||
      (!Number.isNaN(expiry) && expiry < now);
    // Backend health score is 0..10; normalize legacy 0..100 values. Missing → 10 (no alert).
    const rawHealth = a.healthScore;
    const health =
      typeof rawHealth === 'number' && rawHealth > 0
        ? (rawHealth > 10 ? rawHealth / 10 : rawHealth)
        : 10;
    const upgradeAvailable = health < 6.6 && a.lifecycleStatus?.toLowerCase() !== 'replaced';
    if (warrantyExpired) {
      alerts.push({
        assetId: a.id,
        type: 'warranty',
        title: `${a.name} — warranty expired`,
        detail: 'Coverage has lapsed. Renew or add a protection plan.',
      });
    } else if (upgradeAvailable) {
      alerts.push({
        assetId: a.id,
        type: 'upgrade',
        title: `${a.name} — upgrade recommended`,
        detail: `Health score ${health.toFixed(1)}/10 — consider replacing soon.`,
      });
    }
  }
  return alerts;
}

/** Max alerts shown on the dashboard at once. */
const MAX_ALERTS = 2;

export function CriticalAlertsSection({ assets }: Readonly<{ assets: AlertAsset[] }>) {
  const navigate = useNavigate();
  const alerts = computeCriticalAlerts(assets).slice(0, MAX_ALERTS);
  if (alerts.length === 0) return null;

  return (
    <div
      className="rounded-2xl border bg-card overflow-hidden opacity-0 animate-fade-in-up"
      style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b bg-destructive/5">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-destructive/15 flex items-center justify-center">
            <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
          </div>
          <span className="text-sm font-semibold">Critical Alerts</span>
        </div>
        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-destructive text-white">
          {alerts.length}
        </span>
      </div>

      {/* Alert list */}
      <div className="divide-y">
        {alerts.map((al) => {
          const isWarranty = al.type === 'warranty';
          const assetName = al.title.split(' — ')[0];
          return (
            <button
              type="button"
              key={`${al.type}-${al.assetId}`}
              onClick={() =>
                navigate(
                  isWarranty ? `/warranty-plans` : `/assets/${al.assetId}/upgrade`,
                )
              }
              className="w-full text-left px-5 py-4 hover:bg-secondary/30 transition-colors group"
            >
              {/* Type label */}
              <div className="flex items-center gap-1.5 mb-2">
                <div className={`h-1.5 w-1.5 rounded-full ${isWarranty ? 'bg-destructive' : 'bg-amber-500'}`} />
                <span className={`text-[10px] font-bold uppercase tracking-widest ${isWarranty ? 'text-destructive' : 'text-amber-600'}`}>
                  {isWarranty ? 'Warranty Expired' : 'Upgrade Recommended'}
                </span>
              </div>

              {/* Asset name */}
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold leading-snug">{assetName}</p>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {al.detail}
                  </p>
                </div>
                {isWarranty ? (
                  <ShieldX className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                )}
              </div>

              {/* Action hint */}
              <p className={`text-xs font-medium mt-2.5 group-hover:underline ${isWarranty ? 'text-destructive' : 'text-amber-600'}`}>
                {isWarranty ? 'Renew protection →' : 'View upgrade options →'}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
